/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021-2022, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import {
  ChangeDetectorRef,
  Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output,
} from '@angular/core';

import { AppConfigService } from '../../common/services/app-config/app-config.service';
import { ResizedEvent } from './resize-observer.directive';

/**
 * Enumeration for the different options for showing image attribution.
 */
export enum CreditType {
  /**
   * Show the attribution based on the width of the image.
   * Label is shown if image is wide enough, icon shown otherwise.
   * Nothing is shown if there is no attribution.
   */
  AUTO = 'AUTO',

  /**
   * Always show the attribution circular if the image has an attribution.
   */
  CIRCULAR = 'CIRCULAR',

  /**
   * Always show the attribution icon if the image has an attribution.
   */
  ICON = 'ICON',

  /**
   * Don't show any attribution.
   */
  NONE = 'NONE',
}

/**
 * Component to display image assets, including displaying attributions for unsplash images.
 */
@Component({
  selector: 'bulb-image',
  templateUrl: './image.component.html',
  standalone: false,
})
export class ImageComponent implements OnInit, OnChanges {
  /**
   * Id of the image which will be used to fetch image.
   */
  @Input() imageId: string;

  /**
   * The asset of the image to display.
   */
  @Input() imageAsset: any;

  /**
   * Max height of image in carousel.
   */
  @Input() maxHeight: any;

  /**
   * Height of image.
   */
  @Input() height: any;

  /**
   * Orientation of image.
   */
  @Input() orientation: string;

  /**
   * Whether or not the current page the image is on is being edited
   */
  @Input() isEditingPage?: boolean;

  /**
   * How to display the image credit if it exists.
   */
  @Input() creditType: CreditType = CreditType.AUTO;

  /**
   * Whether we should render the credit HTML elements for the image.
   */
  public showCredit: boolean = false;

  /**
   * Credit details to display.
   */
  public creditInfo : any = {};

  /**
   * Whether we should show the credit icon (true) or the credit label (false).
   */
  public showCreditIcon : boolean = false;

  /**
   * Credit icon width breakpoint.
   */
  private static CREDIT_ICON_BREAKPOINT: number = 200;

  /**
   * Constant for our unsplash name limit.
   */
  private static UNSPLASH_NAME_THRESHOLD: number = 10;

  /**
   * Application name for the unsplash url.
   */
  private UNSPLASH_APP_NAME: string = this.appConfig.unsplash.client.application.name;

  /**
   * Unique id used for setting position of credit on image.
   */
  imageCreditId: string;

  /**
   * Check if block is image carousel.
   */
  @Input() isImageCarousel: boolean = false;

  /**
   * Check if it's true to handle swiper event.
   */
  @Input() isContentEditable: boolean = false;

  /**
   * swiperId
   */
  @Input() swiperId: string;

  /**
   * Type of editor.
   */
  @Input() source: string;

  /**
   * show placeholder until cke image get loaded.
   */
  @Input() showPlaceholder: boolean = false;

  /**
   * Show cke image caption once image loaded completely.
   */
  @Output() showCaption: EventEmitter<any> = new EventEmitter();

  /**
   * Update the cke resize handler position.
   */
  @Output() resizeHandler: EventEmitter<any> = new EventEmitter();

  /**
   * Height of image in carousel.
   */
  imageHeight: string;

  /**
   * Constructor.
   *
   * @param BulbAssetLibraryUnsplashService
   * @param AppConfigService
   * @param ChangeDetectorRef
   */
  constructor(
    @Inject('BulbAssetLibraryUnsplashService') private BulbAssetLibraryUnsplashService : any,
    @Inject('BulbBrowserService') private BulbBrowserService : any,
    private appConfig : AppConfigService,
    private cdr: ChangeDetectorRef,
  ) {}

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit() {
    this.updateCreditInfo();
    if (this.orientation === 'FREEFORM') {
      this.imageHeight = this.height;
    } else {
      // Skip if it is mobile device.
      if (!this.BulbBrowserService.isMobile()) {
        this.imageHeight = (this.maxHeight?.height) === '' ? '445px' : this.maxHeight?.height;
      }
    }
    this.imageCreditId = `image-credit-${this.imageAsset.id}`;
    // We only do this during init, because even if the image is resized/rotated/cropped, we won't need to
    // increment the view count again.
    if (this.imageAsset && this.imageAsset.source === 'Unsplash') {
      this.BulbAssetLibraryUnsplashService.updateViewCount(this.imageAsset.imageId);
    }
  }

  /**
   * A lifecycle hook that is called after any data-bound properties is changed of a directive.
   * To handle any additional changes to the component.
   *
   * @param changes in the component
   */
  ngOnChanges() {
    this.updateCreditInfo();
  }

  /**
   * Handle resizing of the image.
   *
   * @param event Size change information.
   */
  onResize(event: ResizedEvent) {
    this.resizeHandler.emit(
      {
        event, imageId: this.imageId, imageCreditId: this.imageCreditId, imageAsset: this.imageAsset,
      },
    );
    if ((event.newRect.width !== event.oldRect?.width) || (event.newRect.height !== event.oldRect?.height)) {
      this.updateCreditDisplay(event.newRect.width);
    }
  }

  /**
   * Update the credit info object based on information from the imageAsset.
   */
  private updateCreditInfo() {
    if (this.imageAsset.source) {
      const utParam = `?utm_source=${this.UNSPLASH_APP_NAME}&utm_medium=referral`;
      this.creditInfo = {
        photographerName: this.trimPhotographerName(this.imageAsset.photographerName),
        photographerProfileUrl: this.imageAsset.photographerProfileUrl + utParam,
        sourceUrl: `https://unsplash.com${utParam}`,
        source: this.imageAsset.source,
      };
    } else {
      this.creditInfo = {};
    }
  }

  /**
   * Update the display to correctly show the correctly attribution if appropriate.
   *
   * @param imgWidth Width of the image used to determine whether to show attribution labels or icon.
   */
  private updateCreditDisplay(imgWidth) {
    this.showCredit = (this.creditInfo.source !== undefined);

    if (this.showCredit) {
      switch (this.creditType) {
        case CreditType.AUTO:
          this.showCreditIcon = (imgWidth < ImageComponent.CREDIT_ICON_BREAKPOINT);
          break;

        case CreditType.CIRCULAR:
          this.showCreditIcon = false;
          break;

        case CreditType.ICON:
          this.showCreditIcon = true;
          break;

        case CreditType.NONE:
        default:
          this.showCredit = false;
          break;
      }
    }
  }

  /**
   * Trim the photographer name.
   *
   * @param photographerName is the name of the photographer.
   */
  private trimPhotographerName(photographerName: string) {
    if (photographerName.length >= ImageComponent.UNSPLASH_NAME_THRESHOLD
      && this.creditType === CreditType.CIRCULAR) {
      return photographerName.substring(0, ImageComponent.UNSPLASH_NAME_THRESHOLD);
    }
    return photographerName;
  }

  /**
   * Brings caption back and remove image placeholder once image gets loaded.
   */
  imageLoaded() {
    this.cdr.detectChanges();

    // This is applying make click event work for ckeditor to show toolbar as swiper prevent event.
    if (this.isImageCarousel && this.isContentEditable) {
      document.querySelectorAll(`[id="${this.swiperId}"]`)[0].setAttribute('contenteditable', 'true');
    }
    this.showCaption.emit();
  }
}
